// /admin/part-families/:id: one part family and the parts in it, edited like a
// parts list with no quantities: the family is the list, a member is a line.
//
// The page holds ONE FamilyDetail. Every write (rename, a member added, moved or
// removed) is followed by a refetch, because the answer carries fresh stock and
// the counted order. Membership lives on the part (`part_catalog_id.part_family_id`),
// so add, move and remove are all a PATCH of that one field on the part.
//
// Family editors may rename, describe and change members. Active and the
// stale-box days are admin-only on the server (403 admin_only_field), so those
// controls are not drawn for anyone else.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import BackLink from '../components/BackLink';
import ItemPicker from '../components/ItemPicker';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import { laneLabel } from '../components/review/fieldStyles';
import { DataTable } from '../components/table/DataTable';
import { useToast } from '../components/Toast/ToastContext';
import { bomErrorMessage, bomErrorStatus, bomPath, type BomMember } from '../Parts/bomsApi';
import { canEditFamilies, isFamilyAdmin } from '../Parts/familyAccess';
import type { PartsCatalogRow } from '../Receiving/types';
import useUserStore from '../stores/useUserStore';
import type { FamilyDetail, FamilyPatch } from './partFamilyTypes';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import Switch from '../components/ui/Switch';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import Alert from '../components/ui/Alert';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
const PICKER_ID = 'family-add-part';
const STALE_ID = 'family-stale-days';
const DEFAULT_LEDE = 'Parts that can stand in for each other in one spot on a drone.';

const memberName = (m: BomMember) => m.base_name || m.display_name || m.sku;
const pickedName = (r: PartsCatalogRow) => r.display_name || r.base_name || r.sku;

// ── The parts table: module-scope columns ────────────────────────────────────
//
// Contract 1 (shared-table.md): the columns never change identity. What moves
// per render (which row asks to be removed, which is saving) arrives on the
// table's `meta`. No cell here may be wrapped in React.memo.

interface MemberMeta {
  confirmingId: number | null;
  removingId: number | null;
  onAskRemove: (partId: number | null) => void;
  onRemove: (m: BomMember) => void;
}

const metaOf = (info: { table: { options: { meta?: unknown } } }) =>
  info.table.options.meta as MemberMeta;

const RemoveCell: React.FC<{ m: BomMember; meta: MemberMeta }> = ({ m, meta }) => {
  const id = m.part_catalog_id;
  if (meta.removingId === id) {
    return <span className="text-xs text-on-surface-variant">Removing…</span>;
  }
  if (meta.confirmingId === id) {
    return (
      <div
        className="flex items-center justify-end gap-1.5"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            meta.onAskRemove(null);
          }
        }}
      >
        <span className="text-xs text-on-surface-variant">Remove from family?</span>
        <button
          type="button"
          autoFocus
          onClick={() => meta.onRemove(m)}
          className={buttonClass('danger', 'sm')}
        >
          Remove
        </button>
        <button
          type="button"
          onClick={() => meta.onAskRemove(null)}
          className={buttonClass('quiet', 'sm')}
        >
          Keep
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => meta.onAskRemove(id)}
      aria-label={`Remove ${memberName(m)}`}
      className={buttonClass('quiet', 'sm')}
    >
      Remove
    </button>
  );
};

const MEMBER_COLUMNS: ColumnDef<BomMember, any>[] = [
  {
    id: 'name',
    header: 'Name',
    meta: { cellClass: 'max-w-[28rem]' },
    cell: ({ row }) => {
      const m = row.original;
      return (
        <div className="flex min-w-0 items-baseline gap-2">
          <Link
            to={`/parts-catalog/${m.part_catalog_id}`}
            className={`truncate rounded font-medium text-on-surface hover:underline ${FOCUS}`}
          >
            {memberName(m)}
          </Link>
          {m.variant_detail && (
            <span className="truncate text-xs text-on-surface-variant">{m.variant_detail}</span>
          )}
        </div>
      );
    },
  },
  {
    id: 'sku',
    header: 'SKU',
    meta: { cellClass: 'font-mono text-xs text-on-surface-variant' },
    cell: ({ row }) => row.original.sku,
  },
  {
    id: 'stock',
    header: 'Stock',
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => (
      <span className={row.original.counted ? 'text-on-surface' : 'text-on-surface-variant'}>
        {row.original.stock}
      </span>
    ),
  },
  {
    id: 'counted',
    header: 'Counted',
    cell: ({ row }) =>
      row.original.counted ? (
        <span className="text-on-surface">Counted</span>
      ) : (
        <span className="text-on-surface-variant">
          Not counted: {row.original.not_counted_reason ?? 'retired'}
        </span>
      ),
  },
  {
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { align: 'right' },
    cell: (info) => <RemoveCell m={info.row.original} meta={metaOf(info)} />,
  },
];

const getMemberId = (m: BomMember) => String(m.part_catalog_id);

// ── States ───────────────────────────────────────────────────────────────────

const Loading: React.FC = () => (
  <PageLayout kind="record" className="pt-4">
    <LogoSpinner label="Loading the part family" />
  </PageLayout>
);

const Notice: React.FC<{ tone?: 'error' | 'info'; children: React.ReactNode }> = ({ tone = 'error', children }) => (
  <PageLayout kind="record" className="pt-4">
    <Alert tone={tone} live="alert" icon={false}>
      {children}
    </Alert>
  </PageLayout>
);


// ── Name and description ─────────────────────────────────────────────────────

const RenameForm: React.FC<{
  label: string;
  onSave: (label: string) => Promise<void>;
  onClose: () => void;
}> = ({ label, onSave, onClose }) => {
  const [name, setName] = useState(label);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const next = name.trim();
    if (!next) {
      setError('A family needs a name.');
      return;
    }
    if (next === label) {
      onClose();
      return;
    }
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(next);
      onClose();
    } catch (err) {
      setError(bomErrorMessage(err, 'Could not rename the family. Try again.'));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-72 max-w-full">
          <Field
            autoFocus
            value={name}
            maxLength={80}
            disabled={busy}
            aria-label="Family name"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                save();
              } else if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
              }
            }}
          />
        </div>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className={buttonClass('filled')}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className={buttonClass('quiet')}
        >
          Cancel
        </button>
      </div>
      <p className="text-xs text-on-surface-variant">Renames this family everywhere.</p>
      <Alert show={!!error} variant="text" tone="error">
        {error}
      </Alert>
    </div>
  );
};

const DescriptionEditor: React.FC<{
  description: string | null;
  onSave: (description: string | null) => Promise<void>;
}> = ({ description, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Enter and Escape both end in a blur; the first finish wins.
  const finished = useRef(false);

  if (editing) {
    const finish = async (save: boolean) => {
      if (finished.current) return;
      finished.current = true;
      setEditing(false);
      const next = draft.trim() === '' ? null : draft.trim();
      if (!save || next === (description ?? null)) return;
      setError(null);
      try {
        await onSave(next);
      } catch (err) {
        setError(bomErrorMessage(err, 'The description was not saved. Try again.'));
      }
    };
    return (
      <div className="w-full max-w-xl">
        <Field
          autoFocus
          value={draft}
          aria-label="Description"
          placeholder="Add a description"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => finish(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              finish(true);
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              finish(false);
            }
          }}
        />
      </div>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2">
      <span className={description ? 'text-on-surface' : ''}>{description || DEFAULT_LEDE}</span>
      <button
        type="button"
        onClick={() => {
          finished.current = false;
          setDraft(description ?? '');
          setError(null);
          setEditing(true);
        }}
        className={buttonClass('quiet', 'sm')}
      >
        {description ? 'Edit description' : 'Add a description'}
      </button>
      <div className="basis-full">
        <Alert show={!!error} variant="text" tone="error">
          Not saved: {error}
        </Alert>
      </div>
    </span>
  );
};

// ── Facts row ────────────────────────────────────────────────────────────────

const ActiveSwitch: React.FC<{ on: boolean; onToggle: (next: boolean) => void }> = ({
  on,
  onToggle,
}) => <Switch checked={on} onChange={(e) => onToggle(e.target.checked)} label="Active" />;

const StaleField: React.FC<{
  days: number | null;
  onSave: (days: number | null) => Promise<void>;
  onInvalid: () => void;
}> = ({ days, onSave, onInvalid }) => {
  const saved = days == null ? '' : String(days);
  const [draft, setDraft] = useState(saved);
  const cancelled = useRef(false);

  useEffect(() => {
    setDraft(saved);
  }, [saved]);

  const commit = async () => {
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    const text = draft.trim();
    if (text === saved) return;
    const n = text === '' ? null : Number(text);
    if (n !== null && (!Number.isInteger(n) || n < 0 || n > 3650)) {
      setDraft(saved);
      onInvalid();
      return;
    }
    try {
      await onSave(n);
    } catch {
      setDraft(saved);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <label htmlFor={STALE_ID} className="text-sm text-on-surface-variant">
        Stale box alert, days
      </label>
      <div className="w-20">
        <Field
          id={STALE_ID}
          type="number"
          inputMode="numeric"
          min={0}
          max={3650}
          value={draft}
          placeholder="None"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              cancelled.current = true;
              setDraft(saved);
              e.currentTarget.blur();
            }
          }}
          className="text-right"
        />
      </div>
    </div>
  );
};

// ── The add row ──────────────────────────────────────────────────────────────

const AddPart: React.FC<{
  memberIds: number[];
  onAdd: (part: PartsCatalogRow) => Promise<void>;
}> = ({ memberIds, onAdd }) => {
  const [moving, setMoving] = useState<PartsCatalogRow | null>(null);
  const [busy, setBusy] = useState<PartsCatalogRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumping this remounts the picker (clearing its typed text) and, through the
  // effect below, hands focus back to it so the next part is one keystroke away.
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (resetKey === 0) return;
    document.getElementById(PICKER_ID)?.focus();
  }, [resetKey]);

  const save = async (part: PartsCatalogRow) => {
    if (busy) return;
    setBusy(part);
    setError(null);
    try {
      await onAdd(part);
      setMoving(null);
      // Idle again BEFORE the reset: the focus effect fires on the reset render,
      // and a still-disabled picker cannot take focus.
      setBusy(null);
      setResetKey((k) => k + 1);
    } catch (err) {
      setError(bomErrorMessage(err, 'The part was not added. Try again.'));
      setBusy(null);
    }
  };

  const picked = (row: PartsCatalogRow | null) => {
    setError(null);
    if (!row) {
      setMoving(null);
      return;
    }
    // A part that sits in another family is MOVED, so ask first and name where
    // it is now. A part with no family has nothing to lose.
    if (row.part_family_id != null) setMoving(row);
    else save(row);
  };

  const cancelMove = () => {
    setMoving(null);
    setResetKey((k) => k + 1);
  };

  return (
    <section aria-label="Add a part to this family" className="rounded-md bg-surface-container-low p-4">
      <h2 className="mb-3 text-sm font-semibold text-on-surface">Add a part</h2>
      <div className="max-w-xl">
        <label htmlFor={PICKER_ID} className={laneLabel}>
          Part
        </label>
        <ItemPicker
          key={resetKey}
          id={PICKER_ID}
          value={moving}
          disabled={busy !== null}
          excludeIds={memberIds}
          onSelect={picked}
        />
      </div>

      <Reveal show={!!moving}>
        <div
          role="group"
          aria-label="Move this part"
          className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md bg-surface-container-high px-3 py-2 text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !busy) {
              e.stopPropagation();
              cancelMove();
            }
          }}
        >
          <span className="text-on-surface">
            {moving && pickedName(moving)} is in {moving?.family_label || 'another family'}. Move it here?
          </span>
          <span className="flex items-center gap-1.5">
            <button
              type="button"
              autoFocus
              disabled={busy !== null}
              onClick={() => moving && save(moving)}
              className={buttonClass('filled', 'sm')}
            >
              Move here
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={cancelMove}
              className={buttonClass('quiet', 'sm')}
            >
              Cancel
            </button>
          </span>
        </div>
      </Reveal>

      <Reveal show={!!busy}>
        <p role="status" className="mt-3 text-sm text-on-surface-variant">
          {busy && (busy.part_family_id != null ? 'Moving' : 'Adding')} {busy && pickedName(busy)}…
        </p>
      </Reveal>
      <Alert show={!!error} variant="text" tone="error" className="mt-3">
        Not added: {error}
      </Alert>
    </section>
  );
};

// ── The page ─────────────────────────────────────────────────────────────────

type Status = 'loading' | 'ready' | 'error' | 'notfound';

const PartFamilyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);
  const canEdit = canEditFamilies(user);
  const isAdmin = isFamilyAdmin(user);

  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState('');
  const [detail, setDetail] = useState<FamilyDetail | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [activeBusy, setActiveBusy] = useState(false);
  const renameWrap = useRef<HTMLSpanElement>(null);

  // Only the latest request may write state: a slow answer for a family the URL
  // has moved off, or one that a newer refetch has overtaken, is dropped.
  const seq = useRef(0);

  const fetchDetail = useCallback(async () => {
    const mine = ++seq.current;
    const d = (await api.get(`/part-families/${id}`)) as FamilyDetail;
    if (mine !== seq.current) return false;
    setDetail(d);
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      if (await fetchDetail()) {
        setConfirmingId(null);
        setStatus('ready');
      }
    } catch (err) {
      if (bomErrorStatus(err) === 404) setStatus('notfound');
      else {
        setLoadError(bomErrorMessage(err, 'The part family did not load.'));
        setStatus('error');
      }
    }
  }, [fetchDetail]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    try {
      await fetchDetail();
    } catch {
      toast.error('Saved, but the page did not refresh. Reload to see the latest.');
    }
  };

  const patchFamily = async (patch: FamilyPatch) => {
    await api.patch(`/part-families/${id}`, { body: patch });
    await refresh();
  };

  const setPartFamily = async (partId: number, familyId: number | null) => {
    await api.patch(`/parts-catalog/${partId}`, { body: { part_family_id: familyId } });
    await refresh();
  };

  const onRemove = async (m: BomMember) => {
    setConfirmingId(null);
    setRemovingId(m.part_catalog_id);
    try {
      await setPartFamily(m.part_catalog_id, null);
    } catch (err) {
      toast.error(bomErrorMessage(err, 'The part was not removed. Try again.'));
    } finally {
      setRemovingId(null);
    }
  };

  const onAdd = async (part: PartsCatalogRow) => {
    if (!detail) return;
    await setPartFamily(part.id, detail.family.id);
  };

  const onToggleActive = async (next: boolean) => {
    if (activeBusy) return;
    setActiveBusy(true);
    try {
      await patchFamily({ is_active: next });
    } catch (err) {
      toast.error(bomErrorMessage(err, 'Could not change whether the family is active. Try again.'));
    } finally {
      setActiveBusy(false);
    }
  };

  const onSaveStale = async (days: number | null) => {
    try {
      await patchFamily({ stale_box_alert_days: days });
    } catch (err) {
      toast.error(bomErrorMessage(err, 'The stale box alert was not saved. Try again.'));
      throw err;
    }
  };

  const closeRename = () => {
    setRenaming(false);
    setTimeout(() => renameWrap.current?.querySelector('button')?.focus(), 0);
  };

  const memberIds = useMemo(
    () => (detail?.members ?? []).map((m) => m.part_catalog_id),
    [detail]
  );
  const columnVisibility = useMemo(() => ({ actions: canEdit }), [canEdit]);

  if (status === 'loading') return <Loading />;

  if (status === 'notfound') {
    return (
      <Notice tone="info">
        <p className="text-on-surface">There is no part family with that address.</p>
        <p className="mt-1 text-on-surface-variant">
          It may have been removed. Pick one from the list of families.
        </p>
        <BackLink to="/admin/part-families" label="Part Families" className="mt-3" />
      </Notice>
    );
  }

  if (status === 'error' || !detail) {
    return (
      <Notice>
        <p className="text-on-surface">{loadError || 'The part family did not load.'}</p>
        <button type="button" onClick={load} className={`mt-3 ${buttonClass('quiet')}`}>
          Try again
        </button>
      </Notice>
    );
  }

  const { family, members, used_by } = detail;
  const countedStock = members.reduce((sum, m) => sum + (m.counted ? m.stock : 0), 0);
  const summary = `${members.length} ${members.length === 1 ? 'part' : 'parts'}, ${countedStock} in stock`;
  const memberMeta: MemberMeta = {
    confirmingId,
    removingId,
    onAskRemove: setConfirmingId,
    onRemove,
  };

  return (
    <PageLayout kind="record" className="pt-4 space-y-5">
      <PageHeader
        title={family.label}
        helpTitle="About a part family"
        lede={
          canEdit ? (
            <DescriptionEditor
              key={family.id}
              description={family.description}
              onSave={(description) => patchFamily({ description })}
            />
          ) : (
            family.description || DEFAULT_LEDE
          )
        }
        meta={
          renaming ? (
            <RenameForm
              label={family.label}
              onSave={(label) => patchFamily({ label })}
              onClose={closeRename}
            />
          ) : undefined
        }
        help={
          <>
            <p>
              A family is a set of parts that can stand in for each other in one spot on a drone.
              A parts list line that names the family accepts any of them, and their stock counts
              as one pool.
            </p>
            <p className="text-on-surface font-medium">Counted</p>
            <p>
              Retired parts and prototypes are listed here but left out of the stock total, so
              the number matches what the shelf can actually build with.
            </p>
            <p className="text-on-surface font-medium">Moving a part</p>
            <p>
              A part belongs to one family. Adding a part that sits in another family moves it
              here after you confirm.
            </p>
          </>
        }
        actions={
          <>
            <SecondaryAction variant="text" to="/admin/part-families">
              All part families
            </SecondaryAction>
            {canEdit && (
              <Tooltip label="Renames this family everywhere">
                <span ref={renameWrap}>
                  <SecondaryAction variant="text" onClick={() => setRenaming(true)}>
                    Rename
                  </SecondaryAction>
                </span>
              </Tooltip>
            )}
          </>
        }
      />

      <section
        aria-label="About this family"
        className="flex flex-wrap items-center gap-x-8 gap-y-3 rounded-md bg-surface-container-low px-5 py-4"
      >
        {/* Re-keyed on the answer: the figure lands again only when it moved.
            The one charm moment on this page. */}
        <p
          key={summary}
          aria-live="polite"
          className="text-lg font-semibold tabular-nums text-on-surface motion-safe:animate-rowLand"
        >
          {summary}
        </p>
        <Appear show={!family.is_active}>
          <Tag>Not active</Tag>
        </Appear>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {used_by.length === 0 ? (
            <span className="text-on-surface-variant">Not on any parts list yet</span>
          ) : (
            <>
              <span className="text-on-surface-variant">Used by</span>
              {used_by.map((u) => (
                <Link
                  key={u.product_key}
                  to={bomPath(u.product_key)}
                  className={`rounded-full bg-surface-container-high px-3 py-1 text-xs font-medium text-on-surface hover:bg-surface-container-highest ${FOCUS}`}
                >
                  {u.model_code ?? u.name}
                  {u.label && <span className="font-normal text-on-surface-variant"> {u.label}</span>}
                </Link>
              ))}
            </>
          )}
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 sm:ml-auto">
            <ActiveSwitch on={family.is_active} onToggle={onToggleActive} />
            <StaleField
              days={family.stale_box_alert_days}
              onSave={onSaveStale}
              onInvalid={() => toast.error('Stale box alert is a whole number of days, 0 to 3650.')}
            />
          </div>
        )}
      </section>

      <DataTable<BomMember>
        data={members}
        columns={MEMBER_COLUMNS}
        getRowId={getMemberId}
        columnVisibility={columnVisibility}
        meta={memberMeta}
        empty={
          <EmptyState variant="area">
            No parts in this family yet. Add the first one below.
          </EmptyState>
        }
      />

      {canEdit && <AddPart memberIds={memberIds} onAdd={onAdd} />}
    </PageLayout>
  );
};

export default PartFamilyPage;
